import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { PowerPickTimeZoneToLocal } from "/app/src/components/generic/formatting/dateTime";
import { costCenterService } from "/app/src/services";

import Search from "../search";

import { useTranslation } from "react-i18next";
import { CostCenter as CostCenterType } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
export default function CostCenters() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const columnHelper = createColumnHelper<CostCenterType>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        cell: (info) => info.getValue(),
        header: () => t("translation:name"),
      }),
      columnHelper.accessor("description", {
        id: "description",
        cell: (info) => info.getValue(),
        header: () => t("translation:description"),
      }),
      columnHelper.accessor("creationDate", {
        id: "creationDate",
        cell: (info) => {
          return (
            <PowerPickTimeZoneToLocal
              date={info.getValue()}
              format={"YYYY-MM-DD HH:mm:ss"}
            />
          );
        },
        header: () => t("translation:creation_date"),
      }),
    ],
    [columnHelper, t],
  );

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useSortUpgrade([]);
  const [searchString, setSearchString] = useState("");

  const goToCostCenter = useCallback(
    (costCenterId: number) => {
      navigate(`/explorer/cost_centers/${costCenterId}`);
    },
    [navigate],
  );

  const {
    data: costCenters,
    count: costCentersCount,
    isFetching,
  } = usePaginatedData<CostCenterType>({
    queryKey: ["cost_centers"],
    searchString,
    page,
    pageSize,
    sort,
    service: costCenterService,
  });

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:cost_centers")} - ItemPath</title>
      </Helmet>
      <div className="search box">
        <Search setSearchString={setSearchString} />
      </div>
      <Table
        loading={isFetching}
        rows={costCenters}
        tableColumns={columns}
        length={costCentersCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage,
          setPageSize,
        }}
        enableRowSelection
        rowClicked={goToCostCenter}
        emptyText={`${t("translation:no")} ${t("translation:cost_centers")} ${t(
          "translation:found",
        )}`}
      />
    </div>
  );
}
